import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';

import { OrderByOption } from 'constants/orderBy';
import { chatAPI } from 'services/api';
import { RootState } from 'store/rootReducer';

import {
  CanalInfoInterface,
  CanalInfoInterfaceV2,
  CanalInterface,
  CanalModoEnvio,
} from './canais';
import { ContatoInterface } from './contatos';
import { EquipeInterface } from './equipes';
import { AutorMensagemTypes } from './mensagens';
import { TagInterface, TagInterfaceV2 } from './tags';
import { UsuarioInterface, UsuarioInterfaceV2 } from './usuarios';

const MAX_ITEMS_VISIVEIS_NA_FILA = 6; // Número máximo de atendimentos visíveis da fila no Header por vez.

export type MessageSocketError = { error: string };

export enum FeatureFlagsEnum {
  NEW_AUDIO_SENDER_LIB = 'new-audio-sender-lib',
  INSTAGRAM_SEND_MESSAGE_TAGGED = 'instagram-send-message-tagged',
}

export type IRules = {
  noRemoveMessage: string;
  noReplyMessage: string;
  noEditMessage: string;
  addSessionControl: boolean;
};

export enum PosicaoAtendimentoEnum {
  API_DISPARO = 'API_DISPARO',
  CAMPANHA_ENVIADA = 'CAMPANHA_ENVIADA',
  BOT = 'BOT',
  ATENDENTE = 'ATENDENTE',
  AGENTE_IA = 'AGENTE_IA',
  EQUIPE = 'EQUIPE',
  AVALIACAO_AT = 'AVALIACAO_AT',
}

export type AtendimentoStatusTransferenciaTypes =
  | 'PENDENTE'
  | 'ACEITO'
  | 'RECUSADO'
  | 'CANCELADO';

export type StatusSessaoTypes = 'OPEN' | 'CLOSED';

export type TransferToTypes = 'EQUIPE' | 'ATENDENTE' | 'AGENT_IA' | 'BOT';

export enum StatusSessaoEnum {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
}

export enum AtendimentoContatoStatusEnum {
  IN_PROGRESS = 'IN_PROGRESS',
  FINISHED = 'FINISHED',
}

export declare interface AtendimentoTransferenciaInterface {
  id: string;
  comentario: string;
  contato: ContatoInterface;
  status: AtendimentoStatusTransferenciaTypes;
  transferidoDe: string;
  transferidoDeFoto: string;
  aceitoPor: string;
  recusadoPor: string;
  canceladoPor: string;
  transferidoPara: string;
}

export declare interface TransferirAtendimentoInterface {
  atendimentoId: string;
  comentario: string;
  criadoEm: string;
  dataStatus: string;
  deUsuarioId: string;
  id: string;
  paraUsuarioId: string;
  status: string;
  tenantId: string;
  transferidoPara: string;
}

export declare interface AtendimentoStatusInterface {
  status:
    | 'iniciado'
    | 'iniciado-por-mim'
    | 'fila-espera'
    | 'finalizado'
    | 'finalizado-por-mim'
    | 'transferencia-forcada'
    | 'transferido-fila-espera'
    | 'transferido-outro-atendente';
  motivo: 'INATIVIDADE_ATENDENTE' | 'INATIVIDADE_CONTATO';
  finalizadoPor: 'ADMINISTRADOR' | 'CORECHAT';
  atendimento: AtendimentoInterface;
  usuarioTransferiu: UsuarioInterface;
}

export type AtendimentoFlagType = 'TRANSFERIDO';

export enum AtendimentoFlagEnum {
  TRANSFERIDO = 'TRANSFERIDO',
}

export type DefaultParams = {
  id: string;
  name: string;
};

export declare interface AtendimentoInterface {
  id: string;
  canal: CanalInterface;
  canalInfo: CanalInfoInterface;
  posicao: 'ATENDENTE';
  canalCodigo: string;
  codigo: string;
  contato: ContatoInterface;
  contatoId: string;
  criadoEm: string;
  criadoPor: string;
  iniciadoEm: string;
  finalizadoEm: string;
  tags: TagInterface[];
  totalMensagensNaoLidas: number;
  usuarioQueFinalizou: UsuarioInterface | undefined;
  transferenciaPendenteId: string | undefined;
  rules: IRules;
  fixadoEm: string | null;
  removidoPorAdminEm: string | null;
  sessaoExpiraEm: string | null;
  sessaoContatoId: string | null;
  sessaoStatus: StatusSessaoTypes | null;
  sessaoTaggedStatus: StatusSessaoTypes | null;
  equipe?: EquipeInterface;
  flag?: AtendimentoFlagType | null;
}

export declare interface AtendimentoInterfaceV2 {
  id: string;
  code: string;
  startedIn: string;
  finishedIn: string;
  durationInSeconds: number;
  finalizadoPor: AutorMensagemTypes;
  team?: DefaultParams;
  attendantCurrent?: DefaultParams;
  attendantFinisher?: DefaultParams;
  botFinisher?: DefaultParams;
  contact?: DefaultParams;
  channel?: CanalInfoInterfaceV2;
  tags?: TagInterfaceV2[];
  hasAccess?: boolean;
  position: PosicaoAtendimentoEnum;
  atendimentoReason?: {
    id: string;
    name: string;
    description: string;
  };
}

export declare interface AtendimentoFilaInterface {
  id: string;
  code: string;
  startedIn: string;
  startedInThisTeam: string;
  team?: DefaultParams;
  position: PosicaoAtendimentoEnum;
  contact: DefaultParams & {
    blockedAt?: string | null;
    bloqueadoEm?: string | null;
    identifier?: string;
    foto?: string;
  };

  channel: CanalInfoInterfaceV2;
  totalMensagensNaoLidas: number;
  tags: TagInterfaceV2[];
  fixedIn: string | null;
}

export declare interface AtendimentoReasonInterface {
  id: string;
  name: string;
  description: string;
  team: {
    id: string;
    name: string;
  } | null;
}

export type FiltersHistoricoParams = {
  message?: string;
  contatoId?: string;
  code?: string;
  participantId?: string;
  dateFinishedStart?: string;
  dateFinishedEnd?: string;
  dateCreatedAtStart?: string;
  dateCreatedAtEnd?: string;
  channelCodes?: string[];
  tagIds?: string[];
  cursor?: CursorInterface;
  status?: string | null;
  reasonId?: string;
};

type AtendimentosState = {
  atendimentoSelecionado: AtendimentoInterface | undefined;
  historicoAtendimentoSelecionado:
    | AtendimentoInterface
    | AtendimentoInterfaceV2
    | undefined;

  historicoAtendimentoNaFila:
    | AtendimentoInterface
    | AtendimentoInterfaceV2
    | AtendimentoFilaInterface
    | undefined;

  filaEspera: AtendimentoInterface[];
  loadingFila: boolean;
  totalFila: number;
  totalRestanteFila: number;

  filaEsperaV2: AtendimentoFilaInterface[];
  loadingFilaV2: boolean;
  totalFilaV2: number;
  totalRestanteFilaV2: number;

  listaAtendimentosNaFila: AtendimentoInterface[];
  listaAtendimentosNaFilaCursor: CursorInterface;
  loadingListaAtendimentosNaFila: boolean;

  listaAtendimentosNaFilaV2: AtendimentoFilaInterface[];
  listaAtendimentosNaFilaV2Cursor: CursorInterface;
  loadingListaAtendimentosNaFilaV2: boolean;

  atendimentosIniciados: AtendimentoInterface[];
  loadingAtendimentosIniciados: boolean;

  totalMessagesUnreadV2: number;

  atendimentosConcluidos: AtendimentoInterface[];

  atendimentosConcluidosCursor: CursorInterface;
  loadingAtendimentosConcluidos: boolean;

  atendimentosFinalizados: AtendimentoInterfaceV2[];
  atendimentosFinalizadosCursor: CursorInterface;
  loadingAtendimentosFinalizados: boolean;
  // requestId da última requisição disparada; respostas de outras são descartadas
  activeAtendimentosFinalizadosRequestId?: string;

  atendimentosEmAndamento: AtendimentoInterfaceV2[];
  atendimentosEmAndamentoCursor: CursorInterface;
  loadingAtendimentosEmAndamento: boolean;
  activeAtendimentosEmAndamentoRequestId?: string;

  atendimentosFinalizadosDoContato: AtendimentoInterfaceV2[];
  atendimentosFinalizadosDoContatoCursor: CursorInterface;
  loadingAtendimentosFinalizadosDoContato: boolean;

  atendimentosEmAndamentoDoContato: AtendimentoInterfaceV2[];
  loadingAtendimentosEmAndamentoDoContato: boolean;

  atendimentoDeAtividadeSelecionado?: AtendimentoInterfaceV2;

  atendimentoContato: AtendimentoInterfaceV2 | null;
  loadingAtendimentoContato: boolean;

  showModalTransferenciaEnviada: boolean;
  transferenciaRecebida: AtendimentoTransferenciaInterface | undefined;
  transferencias: AtendimentoTransferenciaInterface[];
  listaTransferencias: AtendimentoTransferenciaInterface[];
  atendenteSelecionado: UsuarioInterface | undefined;
  atendenteSelecionadoV2: UsuarioInterfaceV2 | undefined;
  isTransferenciaAceita: boolean;
  transferenciaIniciada: TransferirAtendimentoInterface | undefined;

  loadingAceitarTransferencia: boolean;
  loadingRecusarTransferencia: boolean;
  loadingCancelarTransferencia: boolean;

  loadingIniciarAtendimento: boolean;
  loadingFinalizarAtendimento: boolean;
  loadingFinalizarAtendimentoNaFila: boolean;
  loadingTransferirAtendimento: boolean;
  loadingAddTagAtendimento: boolean;
  loadingRemoveTagAtendimento: boolean;
  loadingFixarAtendimento: boolean;
  loadingDesafixarAtendimento: boolean;

  showModalAlertaInatividade: boolean;
  loadingChecarInatividade: boolean;

  drawerFilaDeEsperaAberto: boolean;
  searchTermFilaDeEspera: string | undefined;
  orderByFilaDeEspera: OrderByOption | undefined;

  reasonsAtendimento: AtendimentoReasonInterface[];
  loadingReasonsAtendimento: boolean;
  isRequiredMotivoAtendimento: boolean;
  loadingFinishReasonSetting: boolean;

  loadingMarcarAtendimentoComoLido: boolean;
  loadingMarcarAtendimentoComoNaoLido: boolean;
  loadingMarcarTodosAtendimentosComoLidos: boolean;
};

// async thunks

export const fetchFila = createAsyncThunk(
  'atendimentos/fetchFila',
  async (params?: { limit?: number; orderBy?: string }) => {
    const response = await chatAPI.get('atendimentos/fila_espera-next', {
      params: { limit: params?.limit ?? MAX_ITEMS_VISIVEIS_NA_FILA, ...params },
    });
    return {
      data: response.data.data as AtendimentoInterface[],
      total: response.data.total as number,
      totalRestante: response.data.totalRestante as number,
    };
  },
);

export const fetchFilaV2 = createAsyncThunk(
  'atendimentos/fetchFilaV2',
  async (params?: { limit?: number; orderBy?: string }) => {
    const response = await chatAPI.get('v2/atendimentos/fila-espera', {
      params: { limit: params?.limit ?? MAX_ITEMS_VISIVEIS_NA_FILA, ...params },
    });

    return {
      data: response.data.data as AtendimentoFilaInterface[],
      total: response.data.total as number,
      totalRestante: response.data.totalRestante as number,
      totalMessagesUnread: response.data.totalMessagesUnread as number,
    };
  },
);

export const fetchAtendimentoReasons = createAsyncThunk(
  'atendimentos/fetchAtendimentoReasons',
  async ({ onlyUserReasons }: { onlyUserReasons?: boolean }) => {
    const response = await chatAPI.get('v2/atendimentos/reasons/select', {
      params: { onlyUserReasons },
    });
    return response.data as AtendimentoReasonInterface[];
  },
);

export const fetchFinishReasonSetting = createAsyncThunk(
  'atendimentos/fetchFinishReasonSetting',
  async () => {
    const response = await chatAPI.get(
      'v2/configurations/finish-reason-setting',
    );
    return response.data as { isRequiredMotivoAtendimento: boolean };
  },
);

export const fetchListaAtendimentosNaFila = createAsyncThunk(
  'atendimentos/fetchListaAtendimentosNaFila',
  async (params?: {
    cursor?: CursorInterface;
    search?: string;
    orderBy?: string;
  }) => {
    const response = await chatAPI.get('atendimentos/fila_espera-next', {
      params: {
        ...params?.cursor,
        search: params?.search,
        orderBy: params?.orderBy,
      },
    });
    return {
      data: response.data.data as AtendimentoInterface[],
      cursor: response.data.cursor as CursorInterface,
    };
  },
);

export const fetchListaAtendimentosNaFilaV2 = createAsyncThunk(
  'atendimentos/fetchListaAtendimentosNaFilaV2',
  async (params?: {
    cursor?: CursorInterface;
    search?: string;
    orderBy?: string;
  }) => {
    const response = await chatAPI.get('v2/atendimentos/fila-espera', {
      params: {
        ...params?.cursor,
        search: params?.search,
        orderBy: params?.orderBy,
      },
    });
    return {
      data: response.data.data as AtendimentoFilaInterface[],
      cursor: response.data.cursor as CursorInterface,
    };
  },
);

export const fetchAtendimentosIniciados = createAsyncThunk(
  'atendimentos/fetchAtendimentosIniciados',
  async (params?: { search?: string; orderBy?: string }) => {
    const response = await chatAPI.get('atendimentos/meus/iniciados', {
      params: {
        ...params,
      },
    });
    return response.data as AtendimentoInterface[];
  },
);

export const fetchAtendimentosConcluidos = createAsyncThunk(
  'atendimentos/fetchAtendimentosConcluidos',
  async (data: {
    contatoId: string;
    filters?: string;
    cursor?: CursorInterface;
  }) => {
    const { contatoId, filters, cursor } = data;

    const response = await chatAPI.get(
      `/atendimentos/contato/${contatoId}/concluidos`,
      { params: { filters, ...cursor } },
    );

    return {
      data: response.data.data as AtendimentoInterface[],
      cursor: response.data.cursor as CursorInterface,
    };
  },
);

// axios 0.21 não aceita AbortSignal; converte o signal do thunk em CancelToken
const cancelTokenFromSignal = (signal: AbortSignal) => {
  const source = axios.CancelToken.source();
  if (signal.aborted) source.cancel();
  else signal.addEventListener('abort', () => source.cancel());
  return source.token;
};

const requestAtendimentos = async (
  params: FiltersHistoricoParams,
  signal?: AbortSignal,
) => {
  const {
    cursor,
    status = AtendimentoContatoStatusEnum.FINISHED,
    ...restParams
  } = params;

  const response = await chatAPI.get('v2/atendimentos', {
    params: {
      status,
      ...restParams,
      ...cursor,
    },
    cancelToken: signal ? cancelTokenFromSignal(signal) : undefined,
  });

  return {
    data: response.data.data as AtendimentoInterfaceV2[],
    cursor: response.data.cursor as CursorInterface,
  };
};

export const fetchAtendimentosFinalizados = createAsyncThunk(
  'v2/atendimentos/fetchAtendimentosFinalizados',
  async (params: FiltersHistoricoParams, { signal }) =>
    requestAtendimentos(params, signal),
);

// Mesma consulta de fetchAtendimentosFinalizados, mas sem escrever no estado
// da listagem do histórico.
export const fetchUltimosAtendimentosDoContato = createAsyncThunk(
  'v2/atendimentos/fetchUltimosAtendimentosDoContato',
  async (params: FiltersHistoricoParams, { signal }) =>
    requestAtendimentos(params, signal),
);

export const fetchAtendimentosEmAndamento = createAsyncThunk(
  'v2/atendimentos/fetchAtendimentosEmAndamento',
  async (params: FiltersHistoricoParams, { signal }) => {
    const { cursor, ...restParams } = params;

    const response = await chatAPI.get('v2/atendimentos', {
      params: {
        status: AtendimentoContatoStatusEnum.IN_PROGRESS,
        ...restParams,
        ...cursor,
      },
      cancelToken: cancelTokenFromSignal(signal),
    });

    return {
      data: response.data.data as AtendimentoInterfaceV2[],
      cursor: response.data.cursor as CursorInterface,
    };
  },
);

export const fetchAtendimentoContato = createAsyncThunk(
  'v2/contatos/fetchAtendimentoContato',
  async (params: { contatoId: string; atendimentoId: string }) => {
    const { contatoId, atendimentoId } = params;
    const response = await chatAPI.get(
      `v2/contacts/${contatoId}/atendimentos/${atendimentoId}`,
    );
    return response.data as AtendimentoInterfaceV2;
  },
);

export const fetchAtendimentosEmAndamentoDoContato = createAsyncThunk(
  'v2/contatos/fetchAtendimentosEmAndamentoDoContato',
  async (params: { contatoId: string }) => {
    const { contatoId } = params;
    const response = await chatAPI.get(
      `v2/contacts/${contatoId}/atendimentos`,
      {
        params: {
          status: AtendimentoContatoStatusEnum.IN_PROGRESS,
        },
      },
    );

    return {
      data: response.data.data as AtendimentoInterfaceV2[],
      total: response.data.total as number,
    };
  },
);

export const fetchAtendimentosFinalizadosDoContato = createAsyncThunk(
  'v2/contatos/fetchAtendimentosFinalizadosDoContato',
  async (params: {
    contatoId: string;
    dateFinishedStart?: string;
    dateFinishedEnd?: string;
    channelCode?: string;
    tagIds?: string[];
    cursor?: CursorInterface;
  }) => {
    const { contatoId, cursor, ...restParams } = params;
    const response = await chatAPI.get(
      `v2/contacts/${contatoId}/atendimentos`,
      {
        params: {
          status: AtendimentoContatoStatusEnum.FINISHED,
          ...cursor,
          ...restParams,
        },
      },
    );

    return {
      data: response.data.data as AtendimentoInterfaceV2[],
      cursor: response.data.cursor as CursorInterface,
    };
  },
);

export const iniciarContato = createAsyncThunk(
  'atendimentos/iniciarContato',
  async (
    data: { contatoId: string; canalId: string; equipeId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await chatAPI.post(
        'atendimentos/novo-atendimento',
        data,
      );

      return response.data?.data?.atendimento as AtendimentoInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const iniciarAtendimento = createAsyncThunk(
  'atendimentos/iniciarAtendimento',
  async (atendimentoId: string, { rejectWithValue }) => {
    try {
      const response = await chatAPI.put(
        `atendimentos/${atendimentoId}/iniciar`,
      );
      return response.data.data?.atendimento as AtendimentoInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const finalizarAtendimento = createAsyncThunk(
  'atendimentos/finalizarAtendimento',
  async (
    data: {
      atendimentoId: string;
      atendimentoReasonId?: string;
      tagIds?: string[];
    },
    { rejectWithValue },
  ) => {
    try {
      const { atendimentoId, ...body } = data;
      const response = await chatAPI.put(
        `atendimentos/${atendimentoId}/finalizar`,
        body,
      );
      return response.data as AtendimentoInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const finalizarAtendimentoNaFila = createAsyncThunk(
  'atendimentos/finalizarAtendimentoNaFila',
  async (
    data: {
      atendimentoId: string;
      atendimentoReasonId?: string;
      tagIds?: string[];
    },
    { rejectWithValue },
  ) => {
    try {
      const { atendimentoId, ...body } = data;
      const response = await chatAPI.put(
        `atendimentos/${atendimentoId}/finalizar-na-fila`,
        body,
      );
      return response.data as AtendimentoInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const transferirAtendimento = createAsyncThunk(
  'atendimentos/transferirAtendimento',
  async (
    data: {
      atendimentoId?: string;
      transferTo?: TransferToTypes;
      teamId?: string;
      atendenteId?: string;
      comment?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const { atendimentoId, ...body } = data;
      const response = await chatAPI.put(
        `v2/atendimentos/${atendimentoId}/transfer`,
        body,
      );

      return {
        data: response.data.data as TransferirAtendimentoInterface,
        params: data,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchAddTagAtendimento = createAsyncThunk(
  'atendimentos/fetchAddTagAtendimento',
  async (
    params: { tagId: string; atendimentoId: string },
    { rejectWithValue },
  ) => {
    try {
      const { tagId, atendimentoId } = params;
      const response = await chatAPI.post(
        `tags/${tagId}/atendimento/${atendimentoId}`,
      );

      return {
        data: response.data.data as TagInterface,
        atendimentoId: atendimentoId as string,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchRemoveTagAtendimento = createAsyncThunk(
  'atendimentos/fetchRemoveTagAtendimento',
  async (
    params: { tagId: string; atendimentoId: string },
    { rejectWithValue },
  ) => {
    try {
      const { tagId, atendimentoId } = params;
      const response = await chatAPI.delete(
        `tags/${tagId}/atendimento/${atendimentoId}`,
      );
      return {
        data: response.data.data as TagInterface,
        tagId,
        atendimentoId,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchAceitarTransferencia = createAsyncThunk(
  'atendimentos/fetchAceitarTransferencia',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await chatAPI.put(
        `/atendimentos/transferencias/${id}/aceitar`,
      );
      return {
        data: response.data.data,
        id,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchRecusarTransferencia = createAsyncThunk(
  'atendimentos/fetchRecusarTransferencia',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await chatAPI.put(
        `/atendimentos/transferencias/${id}/recusar`,
      );
      return {
        data: response.data.data,
        id,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchCancelarTransferencia = createAsyncThunk(
  'atendimentos/fetchCancelarTransferencia',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await chatAPI.put(
        `/atendimentos/transferencias/${id}/cancelar`,
      );
      return {
        message: response.data.message,
        id,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fixarAtendimento = createAsyncThunk(
  'atendimentos/fixarAtendimento',
  async (atendimentoId: string, { rejectWithValue }) => {
    try {
      const response = await chatAPI.put(`atendimentos/${atendimentoId}/fixar`);
      return response.data.data?.atendimento as AtendimentoInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const desafixarAtendimento = createAsyncThunk(
  'atendimentos/desafixarAtendimento',
  async (atendimentoId: string, { rejectWithValue }) => {
    try {
      const response = await chatAPI.put(
        `atendimentos/${atendimentoId}/desafixar`,
      );
      return response.data.data?.atendimento as AtendimentoInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const checkInactivity = createAsyncThunk(
  'atendimentos/checkInactivity',
  async (atendimentoId: string, { rejectWithValue }) => {
    try {
      const response = await chatAPI.get(
        `v2/atendimentos/${atendimentoId}/check-inactivity`,
      );

      return response.data as {
        alertInactivityAttendant: boolean;
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchContato = createAsyncThunk(
  'atendimentos/fetchContato',
  async (contatoId: string, { rejectWithValue }) => {
    try {
      const response = await chatAPI.get(`contatos/${contatoId}`);
      return response.data.data as ContatoInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const clearFlagAtendimento = createAsyncThunk(
  'atendimentos/clearFlagAtendimento',
  async (atendimentoId: string, { rejectWithValue }) => {
    try {
      const response = await chatAPI.put(
        `v2/atendimentos/${atendimentoId}/clear-flag`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchMarcarAtendimentoComoNaoLido = createAsyncThunk(
  'atendimentos/fetchMarcarAtendimentoComoNaoLido',
  async (atendimentoId: string, { rejectWithValue }) => {
    try {
      const response = await chatAPI.put(
        `v2/atendimentos/${atendimentoId}/mark-as-unread`,
      );
      return response.data.data as AtendimentoInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchMarcarAtendimentoComoLido = createAsyncThunk(
  'atendimentos/fetchMarcarAtendimentoComoLido',
  async (atendimentoId: string, { rejectWithValue }) => {
    try {
      const response = await chatAPI.put(
        `v2/atendimentos/${atendimentoId}/mark-as-read`,
      );
      return response.data.data as AtendimentoInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchMarcarTodosAtendimentosComoLidos = createAsyncThunk(
  'atendimentos/fetchMarcarTodosAtendimentosComoLidos',
  async (_, { rejectWithValue }) => {
    try {
      const response = await chatAPI.put('v2/atendimentos/mark-as-read');
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// reducer

const initialState = {
  atendimentoSelecionado: undefined,
  historicoAtendimentoSelecionado: undefined,
  historicoAtendimentoNaFila: undefined,

  filaEspera: [],
  loadingFila: false,
  totalFila: 0,
  totalRestanteFila: 0,

  filaEsperaV2: [],
  loadingFilaV2: false,
  totalFilaV2: 0,
  totalRestanteFilaV2: 0,

  listaAtendimentosNaFila: [],
  listaAtendimentosNaFilaCursor: { afterCursor: '', beforeCursor: '' },
  loadingListaAtendimentosNaFila: false,

  listaAtendimentosNaFilaV2: [],
  listaAtendimentosNaFilaV2Cursor: { afterCursor: '', beforeCursor: '' },
  loadingListaAtendimentosNaFilaV2: false,

  atendimentosIniciados: [],
  loadingAtendimentosIniciados: false,

  totalMessagesUnreadV2: 0,

  atendimentosConcluidos: [],

  atendimentosConcluidosCursor: { afterCursor: '', beforeCursor: '' },
  loadingAtendimentosConcluidos: false,

  atendimentosFinalizados: [],
  atendimentosFinalizadosCursor: { afterCursor: '', beforeCursor: '' },
  loadingAtendimentosFinalizados: false,

  atendimentosEmAndamento: [],
  atendimentosEmAndamentoCursor: { afterCursor: '', beforeCursor: '' },
  loadingAtendimentosEmAndamento: false,

  atendimentosFinalizadosDoContato: [],
  atendimentosFinalizadosDoContatoCursor: { afterCursor: '', beforeCursor: '' },
  loadingAtendimentosFinalizadosDoContato: false,

  atendimentosEmAndamentoDoContato: [],
  loadingAtendimentosEmAndamentoDoContato: false,

  atendimentoDeAtividadeSelecionado: undefined,

  atendimentoContato: null,
  loadingAtendimentoContato: false,

  showModalTransferenciaEnviada: false,
  transferenciaRecebida: undefined,
  transferencias: [],
  listaTransferencias: [],
  atendenteSelecionado: undefined,
  atendenteSelecionadoV2: undefined,
  isTransferenciaAceita: false,
  transferenciaIniciada: undefined,

  loadingAceitarTransferencia: false,
  loadingRecusarTransferencia: false,
  loadingCancelarTransferencia: false,

  loadingIniciarAtendimento: false,
  loadingFinalizarAtendimento: false,
  loadingFinalizarAtendimentoNaFila: false,
  loadingTransferirAtendimento: false,
  loadingAddTagAtendimento: false,
  loadingRemoveTagAtendimento: false,
  loadingFixarAtendimento: false,
  loadingDesafixarAtendimento: false,

  showModalAlertaInatividade: false,
  loadingChecarInatividade: false,

  drawerFilaDeEsperaAberto: false,
  searchTermFilaDeEspera: undefined,
  orderByFilaDeEspera: new URLSearchParams(window.location.search).get(
    'orderBy',
  ) as OrderByOption,

  reasonsAtendimento: [],
  loadingReasonsAtendimento: false,
  isRequiredMotivoAtendimento: false,
  loadingFinishReasonSetting: false,

  loadingMarcarAtendimentoComoLido: false,
  loadingMarcarAtendimentoComoNaoLido: false,
  loadingMarcarTodosAtendimentosComoLidos: false,
} as AtendimentosState;

export const atendimentosSlice = createSlice({
  name: 'atendimentos',
  initialState,
  reducers: {
    setAtendimentoDeAtividadeSelecionado(
      state,
      action: PayloadAction<AtendimentoInterfaceV2>,
    ) {
      state.atendimentoDeAtividadeSelecionado = action.payload;
    },
    setSearchTermFilaDeEspera(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.searchTermFilaDeEspera = action.payload;
    },
    setOrderByFilaDeEspera(
      state,
      action: PayloadAction<OrderByOption | undefined>,
    ) {
      state.orderByFilaDeEspera = action.payload;
    },
    setDrawerFilaDeEsperaAberto(state, action: PayloadAction<boolean>) {
      state.drawerFilaDeEsperaAberto = action.payload;
    },
    setshowModalAlertaInatividade(state, action: PayloadAction<boolean>) {
      state.showModalAlertaInatividade = action.payload;
    },
    resetMostrarAlertaDeInatividade(state) {
      state.showModalAlertaInatividade = false;
    },
    setAtendimentoSelecionado(
      state,
      action: PayloadAction<AtendimentoInterface | undefined>,
    ) {
      state.atendimentoSelecionado = action.payload;
    },
    setHistoricoAtendimentoSelecionado(
      state,
      action: PayloadAction<
        AtendimentoInterface | AtendimentoInterfaceV2 | undefined
      >,
    ) {
      state.historicoAtendimentoSelecionado = action.payload;
    },
    setHistoricoAtendimentoNaFila(
      state,
      action: PayloadAction<
        | AtendimentoInterface
        | AtendimentoInterfaceV2
        | AtendimentoFilaInterface
        | undefined
      >,
    ) {
      state.historicoAtendimentoNaFila = action.payload;
    },
    // Seleciona o atendente [transferência]
    setAtendenteSelecionado(state, action: PayloadAction<UsuarioInterface>) {
      state.atendenteSelecionado = action.payload;
    },
    setAtendenteSelecionadoV2(
      state,
      action: PayloadAction<UsuarioInterfaceV2>,
    ) {
      state.atendenteSelecionadoV2 = action.payload;
    },
    onCloseModalTransferenciaEnviada(state) {
      state.showModalTransferenciaEnviada = false;
    },
    // Seta a transferencia a cada vez que clicar na notificação
    setTransferenciaRecebida(
      state,
      action: PayloadAction<AtendimentoTransferenciaInterface>,
    ) {
      state.transferenciaRecebida = action.payload;
    },
    setLimparTransferenciaRecebida(
      state,
      action: PayloadAction<{
        atendimentoId: string;
      }>,
    ) {
      const { atendimentoId } = action.payload;

      if (
        state.atendimentoSelecionado &&
        state.atendimentoSelecionado.id === atendimentoId
      ) {
        state.atendimentoSelecionado.flag = null;
      }

      state.atendimentosIniciados.forEach(atendimento => {
        if (atendimento.id === atendimentoId) {
          atendimento.flag = null;
        }
      });
    },
    updateListaTransferencias(state, action: PayloadAction<{ id: string }>) {
      state.listaTransferencias = state.listaTransferencias.filter(
        ({ id }) => id !== action.payload.id,
      );
    },
    updateAtendimentoSelecionado(
      state,
      action: PayloadAction<{ transferenciaPendenteId: string }>,
    ) {
      if (state.atendimentoSelecionado) {
        state.atendimentoSelecionado.transferenciaPendenteId =
          action.payload.transferenciaPendenteId;
      }
    },
    updateListaDeIniciados(
      state,
      action: PayloadAction<{
        transferenciaPendenteId: string;
        atendimentoId: string;
      }>,
    ) {
      const { transferenciaPendenteId, atendimentoId } = action.payload;
      state.atendimentosIniciados.forEach(atendimento => {
        if (atendimentoId === atendimento.id)
          atendimento.transferenciaPendenteId = transferenciaPendenteId;
      });
    },
    updateListaDeIniciadosAlterarSessao: (
      state,
      action: PayloadAction<{
        atendimentoId: string;
        sessaoStatus: StatusSessaoTypes | null;
        sessaoContatoId?: string | null;
        sessaoExpiraEm?: string | null;
      }>,
    ) => {
      const { atendimentoId, sessaoStatus, sessaoContatoId, sessaoExpiraEm } =
        action.payload;

      state.atendimentosIniciados.forEach(atendimento => {
        if (atendimentoId === atendimento.id) {
          atendimento.sessaoStatus = sessaoStatus;
          atendimento.sessaoContatoId = sessaoContatoId ?? null;
          atendimento.sessaoExpiraEm = sessaoExpiraEm ?? null;
        }

        if (state.atendimentoSelecionado?.id === atendimentoId) {
          state.atendimentoSelecionado.sessaoStatus = sessaoStatus;
          state.atendimentoSelecionado.sessaoContatoId =
            sessaoContatoId ?? null;
          state.atendimentoSelecionado.sessaoExpiraEm = sessaoExpiraEm ?? null;
        }
      });
    },
    updateSessaoExpiraEm: (
      state,
      action: PayloadAction<{
        atendimentoId: string;
        sessaoExpiraEm: string | null;
      }>,
    ) => {
      const { atendimentoId, sessaoExpiraEm } = action.payload;

      state.atendimentosIniciados.forEach(atendimento => {
        if (atendimento.id === atendimentoId) {
          atendimento.sessaoExpiraEm = sessaoExpiraEm;
        }
      });

      if (state.atendimentoSelecionado?.id === atendimentoId) {
        state.atendimentoSelecionado.sessaoExpiraEm = sessaoExpiraEm;
      }
    },
    updateListaDeIniciadosBloqueioContato: (
      state,
      action: PayloadAction<{ contatoId: string; bloqueadoEm: string | null }>,
    ) => {
      const { contatoId, bloqueadoEm } = action.payload;

      state.atendimentosIniciados.forEach(atendimento => {
        if (atendimento.contato?.id === contatoId) {
          atendimento.contato.bloqueadoEm = bloqueadoEm;
        }
      });

      if (state.atendimentoSelecionado?.contato?.id === contatoId) {
        state.atendimentoSelecionado.contato.bloqueadoEm = bloqueadoEm;
      }
    },
    updateListaDeIniciadosModoEnvio: (
      state,
      action: PayloadAction<{
        channelId: string;
        sendMode: CanalModoEnvio;
      }>,
    ) => {
      const { channelId, sendMode } = action.payload;
      if (!channelId || !sendMode) return;

      const isMatch = (atendimento?: {
        canal?: { id?: string; canalId?: string };
        canalInfo?: { canalId?: string };
      }) => {
        if (!atendimento) return false;
        return atendimento.canal?.id === channelId;
      };

      state.atendimentosIniciados.forEach(atendimento => {
        if (isMatch(atendimento)) {
          if (atendimento.canal) {
            atendimento.canal = {
              ...atendimento.canal,
              modoEnvio: sendMode,
            };
          }
          if (sendMode === 'API_OFICIAL') {
            atendimento.sessaoStatus = StatusSessaoEnum.CLOSED;
          } else if (sendMode === 'API_NAO_OFICIAL') {
            atendimento.sessaoStatus = null;
          }
        }
      });

      if (
        state.atendimentoSelecionado &&
        isMatch(state.atendimentoSelecionado)
      ) {
        if (state.atendimentoSelecionado.canal) {
          state.atendimentoSelecionado.canal = {
            ...state.atendimentoSelecionado.canal,
            modoEnvio: sendMode,
          };
        }
        if (sendMode === 'API_OFICIAL') {
          state.atendimentoSelecionado.sessaoStatus = StatusSessaoEnum.CLOSED;
        } else if (sendMode === 'API_NAO_OFICIAL') {
          state.atendimentoSelecionado.sessaoStatus = null;
        }
      }

      state.filaEspera.forEach(atendimento => {
        if (isMatch(atendimento)) {
          if (atendimento.canal) {
            atendimento.canal = {
              ...atendimento.canal,
              modoEnvio: sendMode,
            };
          }
          if (sendMode === 'API_OFICIAL') {
            atendimento.sessaoStatus = StatusSessaoEnum.CLOSED;
          } else if (sendMode === 'API_NAO_OFICIAL') {
            atendimento.sessaoStatus = null;
          }
        }
      });

      state.listaAtendimentosNaFila.forEach(atendimento => {
        if (isMatch(atendimento)) {
          if (atendimento.canal) {
            atendimento.canal = {
              ...atendimento.canal,
              modoEnvio: sendMode,
            };
          }
          if (sendMode === 'API_OFICIAL') {
            atendimento.sessaoStatus = StatusSessaoEnum.CLOSED;
          } else if (sendMode === 'API_NAO_OFICIAL') {
            atendimento.sessaoStatus = null;
          }
        }
      });
    },
    updateListaDeiniciadosAlterarTagContato: (
      state,
      action: PayloadAction<{
        atendimentoId: string;
        tag?: TagInterface;
        tagId?: string;
      }>,
    ) => {
      const { atendimentoId, tag } = action.payload;
      state.atendimentosIniciados.forEach(atendimento => {
        if (atendimentoId === atendimento.id) {
          if (tag) {
            atendimento.contato.tags.push(tag);
          } else {
            atendimento.contato.tags = atendimento.contato.tags.filter(
              ({ id }) => id !== action.payload.tagId,
            );
          }
        }
      });
    },
    updateListaDeIniciadosCancelarTransferencia(
      state,
      action: PayloadAction<{ atendimentoId: string }>,
    ) {
      const { atendimentoId } = action.payload;
      state.atendimentosIniciados.forEach(atendimento => {
        if (atendimentoId === atendimento.id)
          atendimento.transferenciaPendenteId = undefined;
      });
    },
    checkAtendimentoTransferencias(
      state,
      action: PayloadAction<{
        status: AtendimentoStatusTransferenciaTypes;
        transferencia: AtendimentoTransferenciaInterface;
      }>,
    ) {
      const { status, transferencia } = action.payload;
      const { atendimentoSelecionado } = state;
      const checkAtendimentoSelecionado =
        atendimentoSelecionado &&
        atendimentoSelecionado?.transferenciaPendenteId === transferencia.id;

      if (status === 'PENDENTE') {
        state.transferencias.push(transferencia);

        const itemTransferencia = state.listaTransferencias.find(
          ({ id }) => id === transferencia.id,
        );
        /**
         * Checa se a transferência que veio no evento já está na lista.
         * Ja que caso o websocket desconecte, na reconexão chega o evento novamente.
         * Portanto, é feita a verificação pra evitar duplicação do item na lista
         */
        if (!itemTransferencia) state.listaTransferencias.push(transferencia);
      } else if (status === 'ACEITO') {
        state.transferenciaIniciada = initialState.transferenciaIniciada;
        state.atendimentoSelecionado = initialState.atendimentoSelecionado;
        state.atendimentosIniciados = initialState.atendimentosIniciados;
        state.isTransferenciaAceita = true;
        state.showModalTransferenciaEnviada = false;
        state.transferencias.push(transferencia);
      } else if (status === 'RECUSADO') {
        state.transferenciaIniciada = initialState.transferenciaIniciada;
        state.showModalTransferenciaEnviada = false;
        state.transferencias.push(transferencia);

        /**
         * Checa se o atendimento selecionado está com transferência pendente.
         * E ao recusar, atualiza a informação [transferenciaPendenteId]
         */
        if (checkAtendimentoSelecionado) {
          atendimentoSelecionado.transferenciaPendenteId = undefined;
        }
        /**
         * Percorre a lista de atendimentos iniciados para atualizar o
         * atendimento que foi recusado
         * */
        state.atendimentosIniciados.forEach(atendimento => {
          if (atendimento.transferenciaPendenteId === transferencia.id) {
            atendimento.transferenciaPendenteId = undefined;
          }
        });
      } else if (status === 'CANCELADO') {
        state.listaTransferencias = state.listaTransferencias.filter(
          ({ id }) => id !== transferencia.id,
        );
      }
    },
    setTotalMensagensNaoLidasAtendimentoIniciado(
      state,
      action: PayloadAction<{ atendimentoId: string; total: number }>,
    ) {
      const { atendimentoId, total } = action.payload;
      const indexAtendimentoIniciado = state.atendimentosIniciados.findIndex(
        ({ id }) => id === atendimentoId,
      );

      // atualiza o valor das mensagens nao lidas do atendimento na lista de iniciados
      if (indexAtendimentoIniciado > -1) {
        state.atendimentosIniciados[
          indexAtendimentoIniciado
        ].totalMensagensNaoLidas = total;
      }
      // atualiza o total no atendimento selecionado se ele for o mesmo do contador
      if (state.atendimentoSelecionado?.id === atendimentoId) {
        state.atendimentoSelecionado.totalMensagensNaoLidas = total;
      }
    },
    resetAtendimentoContato(state) {
      state.atendimentoContato = initialState.atendimentoContato;
    },
    resetListaAtendimentosNaFila(state) {
      state.listaAtendimentosNaFila = initialState.listaAtendimentosNaFila;
      state.listaAtendimentosNaFilaCursor =
        initialState.listaAtendimentosNaFilaCursor;
      state.loadingListaAtendimentosNaFila =
        initialState.loadingListaAtendimentosNaFila;
    },
    resetListaAtendimentosNaFilaV2(state) {
      state.listaAtendimentosNaFilaV2 = initialState.listaAtendimentosNaFilaV2;
      state.listaAtendimentosNaFilaV2Cursor =
        initialState.listaAtendimentosNaFilaV2Cursor;
      state.loadingListaAtendimentosNaFilaV2 =
        initialState.loadingListaAtendimentosNaFilaV2;
    },
    resetAtendimentosConcluidos(state) {
      state.atendimentosConcluidos = initialState.atendimentosConcluidos;
      state.atendimentosConcluidosCursor =
        initialState.atendimentosConcluidosCursor;
      state.loadingAtendimentosConcluidos =
        initialState.loadingAtendimentosConcluidos;
    },
    resetAtendimentosFinalizadosDoContato(state) {
      state.atendimentosFinalizadosDoContato =
        initialState.atendimentosFinalizadosDoContato;
      state.atendimentosFinalizadosDoContatoCursor =
        initialState.atendimentosFinalizadosDoContatoCursor;
    },
    resetAtendimentosEmAndamentoDoContato(state) {
      state.atendimentosEmAndamentoDoContato =
        initialState.atendimentosEmAndamentoDoContato;
    },
    resetAtendimentosFinalizados(state) {
      state.atendimentosFinalizados = initialState.atendimentosFinalizados;

      state.atendimentosFinalizadosCursor =
        initialState.atendimentosFinalizadosCursor;

      state.loadingAtendimentosFinalizados =
        initialState.loadingAtendimentosFinalizados;

      state.activeAtendimentosFinalizadosRequestId = undefined;
    },
    resetAtendimentosEmAndamento(state) {
      state.atendimentosEmAndamento = initialState.atendimentosEmAndamento;
      state.atendimentosEmAndamentoCursor =
        initialState.atendimentosEmAndamentoCursor;
      state.loadingAtendimentosEmAndamento =
        initialState.loadingAtendimentosEmAndamento;
      state.activeAtendimentosEmAndamentoRequestId = undefined;
    },
    setMarcarAtendimentoComoLido(state, action: PayloadAction<string>) {
      const atendimentoId = action.payload;

      state.atendimentosIniciados.forEach(atendimento => {
        if (atendimento.id === atendimentoId) {
          atendimento.totalMensagensNaoLidas = 0;
        }
      });
    },
    setMarcarTodosAtendimentosComoLidos(state) {
      state.atendimentosIniciados.forEach(atendimento => {
        atendimento.totalMensagensNaoLidas = 0;
      });
      if (state.atendimentoSelecionado) {
        state.atendimentoSelecionado.totalMensagensNaoLidas = 0;
      }
    },
    setMarcarAtendimentoComoNaoLido(state, action: PayloadAction<string>) {
      const atendimentoId = action.payload;

      state.atendimentosIniciados.forEach(atendimento => {
        if (atendimento.id === atendimentoId) {
          atendimento.totalMensagensNaoLidas = -1;
        }
      });
    },
  },
  extraReducers(builder) {
    // Fetch check inactivity
    builder.addCase(checkInactivity.pending, state => {
      state.loadingChecarInatividade = true;
    });
    builder.addCase(checkInactivity.fulfilled, (state, action) => {
      state.loadingChecarInatividade = false;
      state.showModalAlertaInatividade =
        action.payload.alertInactivityAttendant;
    });
    builder.addCase(checkInactivity.rejected, state => {
      state.loadingChecarInatividade = false;
    });
    // Fetch fila de espera
    builder.addCase(fetchFila.pending, state => {
      state.loadingFila = true;
    });
    builder.addCase(fetchFila.fulfilled, (state, action) => {
      state.filaEspera = action.payload.data;
      state.totalFila = action.payload.total;
      state.totalRestanteFila = action.payload.totalRestante;
      state.loadingFila = false;
    });
    builder.addCase(fetchFilaV2.pending, state => {
      state.loadingFilaV2 = true;
    });
    builder.addCase(fetchFilaV2.fulfilled, (state, action) => {
      state.filaEsperaV2 = action.payload.data;
      state.totalFilaV2 = action.payload.total;
      state.totalRestanteFilaV2 = action.payload.totalRestante;
      state.totalMessagesUnreadV2 = action.payload.totalMessagesUnread;
      state.loadingFilaV2 = false;
    });

    builder.addCase(fetchFila.rejected, state => {
      state.loadingFila = false;
    });
    // Fetch lista de atendimentos na fila de espera
    builder.addCase(fetchListaAtendimentosNaFila.pending, state => {
      state.loadingListaAtendimentosNaFila = true;
    });
    builder.addCase(fetchListaAtendimentosNaFila.fulfilled, (state, action) => {
      const { cursor } = action.meta.arg || {};

      if (!cursor || !cursor.afterCursor) {
        state.listaAtendimentosNaFila = action.payload.data;
      } else {
        state.listaAtendimentosNaFila = state.listaAtendimentosNaFila.concat(
          action.payload.data,
        );
      }

      state.listaAtendimentosNaFilaCursor = action.payload.cursor;
      state.loadingListaAtendimentosNaFila = false;
    });
    builder.addCase(fetchListaAtendimentosNaFila.rejected, state => {
      state.loadingListaAtendimentosNaFila = false;
    });
    // Fetch lista de atendimentos v2 na fila de espera
    builder.addCase(fetchListaAtendimentosNaFilaV2.pending, state => {
      state.loadingListaAtendimentosNaFilaV2 = true;
    });
    builder.addCase(
      fetchListaAtendimentosNaFilaV2.fulfilled,
      (state, action) => {
        const { cursor } = action.meta.arg || {};

        if (!cursor || !cursor.afterCursor) {
          state.listaAtendimentosNaFilaV2 = action.payload.data;
        } else {
          state.listaAtendimentosNaFilaV2 =
            state.listaAtendimentosNaFilaV2.concat(action.payload.data);
        }

        state.listaAtendimentosNaFilaV2Cursor = action.payload.cursor;

        state.loadingListaAtendimentosNaFilaV2 = false;
      },
    );
    builder.addCase(fetchListaAtendimentosNaFilaV2.rejected, state => {
      state.loadingListaAtendimentosNaFilaV2 = false;
    });
    // Fetch atendimentos iniciados
    builder.addCase(fetchAtendimentosIniciados.pending, state => {
      state.loadingAtendimentosIniciados = true;
    });
    builder.addCase(fetchAtendimentosIniciados.fulfilled, (state, action) => {
      state.atendimentosIniciados = action.payload;
      state.loadingAtendimentosIniciados = false;
    });
    // Fetch atendimentos concluidos
    builder.addCase(fetchAtendimentosConcluidos.pending, state => {
      state.loadingAtendimentosConcluidos = true;
    });
    builder.addCase(fetchAtendimentosConcluidos.fulfilled, (state, action) => {
      state.atendimentosConcluidos = state.atendimentosConcluidos.concat(
        action.payload.data,
      );
      state.atendimentosConcluidosCursor = action.payload.cursor;
      state.loadingAtendimentosConcluidos = false;
    });

    // Fetch atendimentos concluidos V2
    builder.addCase(fetchAtendimentosFinalizados.pending, (state, action) => {
      state.loadingAtendimentosFinalizados = true;
      state.activeAtendimentosFinalizadosRequestId = action.meta.requestId;
    });
    builder.addCase(fetchAtendimentosFinalizados.fulfilled, (state, action) => {
      if (
        state.activeAtendimentosFinalizadosRequestId !== action.meta.requestId
      ) {
        return;
      }

      const isPagination = Boolean(action.meta.arg.cursor?.afterCursor);

      state.atendimentosFinalizados = isPagination
        ? state.atendimentosFinalizados.concat(action.payload.data)
        : action.payload.data;
      state.atendimentosFinalizadosCursor = action.payload.cursor;
      state.loadingAtendimentosFinalizados = false;
      state.activeAtendimentosFinalizadosRequestId = undefined;
    });
    builder.addCase(fetchAtendimentosFinalizados.rejected, (state, action) => {
      if (
        state.activeAtendimentosFinalizadosRequestId !== action.meta.requestId
      ) {
        return;
      }

      state.loadingAtendimentosFinalizados = false;
      state.activeAtendimentosFinalizadosRequestId = undefined;
    });

    // Fetch atendimento do contato selecionado v2
    builder.addCase(fetchAtendimentoContato.pending, state => {
      state.loadingAtendimentoContato = true;
    });
    builder.addCase(fetchAtendimentoContato.fulfilled, (state, action) => {
      state.atendimentoContato = action.payload;
      state.loadingAtendimentoContato = false;
    });
    builder.addCase(fetchAtendimentoContato.rejected, state => {
      state.loadingAtendimentoContato = false;
    });

    // Fetch atendimentos em andamento do contato v2
    builder.addCase(fetchAtendimentosEmAndamentoDoContato.pending, state => {
      state.loadingAtendimentosEmAndamentoDoContato = true;
    });
    builder.addCase(
      fetchAtendimentosEmAndamentoDoContato.fulfilled,
      (state, action) => {
        state.atendimentosEmAndamentoDoContato =
          state.atendimentosEmAndamentoDoContato.concat(action.payload.data);
        state.loadingAtendimentosEmAndamentoDoContato = false;
      },
    );
    builder.addCase(fetchAtendimentosEmAndamentoDoContato.rejected, state => {
      state.loadingAtendimentosEmAndamentoDoContato = false;
    });

    // Fetch atendimentos finalizados do contato v2
    builder.addCase(fetchAtendimentosFinalizadosDoContato.pending, state => {
      state.loadingAtendimentosFinalizadosDoContato = true;
    });
    builder.addCase(
      fetchAtendimentosFinalizadosDoContato.fulfilled,
      (state, action) => {
        state.atendimentosFinalizadosDoContato =
          state.atendimentosFinalizadosDoContato.concat(action.payload.data);

        state.atendimentosFinalizadosDoContatoCursor = action.payload.cursor;
        state.loadingAtendimentosFinalizadosDoContato = false;
      },
    );
    builder.addCase(fetchAtendimentosFinalizadosDoContato.rejected, state => {
      state.loadingAtendimentosFinalizadosDoContato = false;
    });
    // Iniciar atendimento
    builder.addCase(iniciarAtendimento.pending, state => {
      state.loadingIniciarAtendimento = true;
    });
    builder.addCase(iniciarAtendimento.fulfilled, state => {
      state.loadingIniciarAtendimento = false;
    });
    builder.addCase(iniciarAtendimento.rejected, state => {
      state.loadingIniciarAtendimento = false;
    });
    // Finalizar atendimento iniciado
    builder.addCase(finalizarAtendimento.pending, state => {
      state.loadingFinalizarAtendimento = true;
    });
    builder.addCase(finalizarAtendimento.fulfilled, (state, action) => {
      state.loadingFinalizarAtendimento = false;
      const { atendimentoId } = action.meta.arg;

      state.atendimentosIniciados = state.atendimentosIniciados.filter(
        atendimento => atendimento.id !== atendimentoId,
      );

      if (state.atendimentoSelecionado?.id === atendimentoId) {
        state.atendimentoSelecionado = undefined;
      }
    });
    builder.addCase(finalizarAtendimento.rejected, state => {
      state.loadingFinalizarAtendimento = false;
    });
    // Finalizar atendimento na fila
    builder.addCase(finalizarAtendimentoNaFila.pending, state => {
      state.loadingFinalizarAtendimentoNaFila = true;
    });
    builder.addCase(finalizarAtendimentoNaFila.fulfilled, (state, action) => {
      state.loadingFinalizarAtendimentoNaFila = false;
      const { atendimentoId } = action.meta.arg;

      state.filaEspera = state.filaEspera.filter(
        atendimento => atendimento.id !== atendimentoId,
      );

      state.listaAtendimentosNaFila = state.listaAtendimentosNaFila.filter(
        atendimento => atendimento.id !== atendimentoId,
      );
    });
    builder.addCase(finalizarAtendimentoNaFila.rejected, state => {
      state.loadingFinalizarAtendimentoNaFila = false;
    });

    // Add tag atendimento
    builder.addCase(fetchAddTagAtendimento.pending, state => {
      state.loadingAddTagAtendimento = true;
    });
    builder.addCase(fetchAddTagAtendimento.fulfilled, (state, action) => {
      const {
        historicoAtendimentoSelecionado,
        atendimentosFinalizados,
        atendimentoSelecionado,
        atendimentosIniciados,
        atendimentosConcluidos,
        atendimentoContato,
        atendimentosEmAndamentoDoContato,
        atendimentosFinalizadosDoContato,
        atendimentosEmAndamento,
      } = state;
      const { data: newTag, atendimentoId } = action.payload;

      /* Adicionar tag a um atendimento [lista de em andamento] */
      if (Array.isArray(atendimentosEmAndamento)) {
        const indexAtendimentoFinalizado = atendimentosEmAndamento.findIndex(
          atendimento => atendimento.id === atendimentoId,
        );
        if (indexAtendimentoFinalizado > -1) {
          const tagV2 = {
            id: newTag.id,
            name: newTag.nome,
            color: newTag.cor,
          };

          atendimentosEmAndamento[indexAtendimentoFinalizado].tags?.push(tagV2);

          if (historicoAtendimentoSelecionado) {
            historicoAtendimentoSelecionado.tags?.push(tagV2);
          }
        }
      }

      /* Add tag a um atendimento selecionado */
      if (atendimentoSelecionado) {
        atendimentoSelecionado.tags.push(newTag);
      }

      /* Add tag a um atendimento [lista de iniciados] */
      if (Array.isArray(atendimentosIniciados)) {
        const indexAtendimentoIniciado = atendimentosIniciados.findIndex(
          atendimento => atendimento.id === atendimentoId,
        );
        if (indexAtendimentoIniciado > -1) {
          atendimentosIniciados[indexAtendimentoIniciado].tags.push(newTag);
        }
      }

      /* Add tag a um atendimento [lista de concluídos] */
      if (Array.isArray(atendimentosConcluidos)) {
        const indexAtendimentoConcluido = atendimentosConcluidos.findIndex(
          atendimento => atendimento.id === atendimentoId,
        );
        if (indexAtendimentoConcluido > -1) {
          atendimentosConcluidos[indexAtendimentoConcluido].tags.push(newTag);
        }
      }

      /* Add tag a um atendimento finalizado */
      if (Array.isArray(atendimentosFinalizados)) {
        const indexAtendimentoFinalizado = atendimentosFinalizados.findIndex(
          atendimento => atendimento.id === atendimentoId,
        );
        if (indexAtendimentoFinalizado > -1) {
          const tagV2 = {
            id: newTag.id,
            name: newTag.nome,
            color: newTag.cor,
          };

          atendimentosFinalizados[indexAtendimentoFinalizado].tags?.push(tagV2);

          if (historicoAtendimentoSelecionado) {
            historicoAtendimentoSelecionado.tags?.push(tagV2);
          }
        }
      }

      /* Add tag a um atendimento [lista de em andamento do contato] */
      if (Array.isArray(atendimentosEmAndamentoDoContato)) {
        const indexAtendimentoEmAndamentoDoContato =
          atendimentosEmAndamentoDoContato.findIndex(
            atendimento => atendimento.id === atendimentoId,
          );

        if (indexAtendimentoEmAndamentoDoContato > -1) {
          const tagV2 = {
            id: newTag.id,
            name: newTag.nome,
            color: newTag.cor,
          };

          atendimentosEmAndamentoDoContato[
            indexAtendimentoEmAndamentoDoContato
          ].tags?.push(tagV2);
        }
      }

      /* Add tag a um atendimento [lista de finalizados do contato] */
      if (Array.isArray(atendimentosFinalizadosDoContato)) {
        const indexAtendimentoFinalizadoDoContato =
          atendimentosFinalizadosDoContato.findIndex(
            atendimento => atendimento.id === atendimentoId,
          );

        if (indexAtendimentoFinalizadoDoContato > -1) {
          const tagV2 = {
            id: newTag.id,
            name: newTag.nome,
            color: newTag.cor,
          };

          atendimentosFinalizadosDoContato[
            indexAtendimentoFinalizadoDoContato
          ].tags?.push(tagV2);
        }
      }

      /* Adicionando a tag do contato [contato selecionado v2] */
      if (atendimentoContato) {
        const tagV2 = {
          id: newTag.id,
          name: newTag.nome,
          color: newTag.cor,
        };

        atendimentoContato.tags?.push(tagV2);
      }

      state.loadingAddTagAtendimento = false;
    });
    builder.addCase(fetchAddTagAtendimento.rejected, state => {
      state.loadingAddTagAtendimento = false;
    });

    // Remover tag atendimento
    builder.addCase(fetchRemoveTagAtendimento.pending, state => {
      state.loadingRemoveTagAtendimento = true;
    });
    builder.addCase(fetchRemoveTagAtendimento.fulfilled, (state, action) => {
      const {
        historicoAtendimentoSelecionado,
        atendimentosFinalizados,
        atendimentoSelecionado,
        atendimentosIniciados,
        atendimentosConcluidos,
        atendimentoContato,
        atendimentosFinalizadosDoContato,
        atendimentosEmAndamentoDoContato,
        atendimentosEmAndamento,
      } = state;
      const { tagId, atendimentoId } = action.payload;

      /* Remove a tag do atendimento [lista de em andamento] */
      if (Array.isArray(atendimentosEmAndamento)) {
        const indexAtendimentoEmAndamento = atendimentosEmAndamento.findIndex(
          atendimento => atendimento.id === atendimentoId,
        );
        if (indexAtendimentoEmAndamento > -1) {
          atendimentosEmAndamento[indexAtendimentoEmAndamento].tags =
            atendimentosEmAndamento[indexAtendimentoEmAndamento].tags?.filter(
              tag => tag.id !== tagId,
            );
        }
      }

      /* Remove a tag do atendimento selecionado */
      if (atendimentoSelecionado) {
        atendimentoSelecionado.tags = atendimentoSelecionado.tags.filter(
          tag => tag.id !== tagId,
        );
      }

      /* Remove a tag de um atendimento iniciado */
      if (Array.isArray(atendimentosIniciados)) {
        const indexAtendimentoIniciado = atendimentosIniciados.findIndex(
          atendimento => atendimento.id === atendimentoId,
        );
        if (indexAtendimentoIniciado > -1) {
          atendimentosIniciados[indexAtendimentoIniciado].tags =
            atendimentosIniciados[indexAtendimentoIniciado].tags.filter(
              tag => tag.id !== tagId,
            );
        }
      }

      /* Remove a tag de atendimento concluído */
      if (Array.isArray(atendimentosConcluidos)) {
        const indexAtendimentoConcluido = atendimentosConcluidos.findIndex(
          atendimento => atendimento.id === atendimentoId,
        );

        if (indexAtendimentoConcluido > -1) {
          atendimentosConcluidos[indexAtendimentoConcluido].tags =
            atendimentosConcluidos[indexAtendimentoConcluido].tags.filter(
              tag => tag.id !== tagId,
            );
        }
      }

      /* Remove a tag do atendimento finalizado */
      if (Array.isArray(atendimentosFinalizados)) {
        const indexAtendimentoFinalizado = atendimentosFinalizados.findIndex(
          atendimento => atendimento.id === atendimentoId,
        );

        if (indexAtendimentoFinalizado > -1) {
          atendimentosFinalizados[indexAtendimentoFinalizado].tags =
            atendimentosFinalizados[indexAtendimentoFinalizado].tags?.filter(
              tag => tag.id !== tagId,
            );
        }

        if (historicoAtendimentoSelecionado?.tags) {
          const indexHistoricoAtendimento =
            historicoAtendimentoSelecionado.tags.findIndex(
              tag => tag.id === tagId,
            );

          if (indexHistoricoAtendimento > -1) {
            historicoAtendimentoSelecionado.tags.splice(
              indexHistoricoAtendimento,
              1,
            );
          }
        }
      }

      /* Remove a tag do atendimento [lista de em andamento do contato] */
      if (Array.isArray(atendimentosEmAndamentoDoContato)) {
        const index = atendimentosEmAndamentoDoContato.findIndex(
          atendimento => atendimento.id === atendimentoId,
        );

        if (index > -1) {
          atendimentosEmAndamentoDoContato[index].tags =
            atendimentosEmAndamentoDoContato[index].tags?.filter(
              tag => tag.id !== tagId,
            );
        }
      }

      /* Remove a tag do atendimento [lista de finalizados do contato] */
      if (Array.isArray(atendimentosFinalizadosDoContato)) {
        const index = atendimentosFinalizadosDoContato.findIndex(
          atendimento => atendimento.id === atendimentoId,
        );

        if (index > -1) {
          atendimentosFinalizadosDoContato[index].tags =
            atendimentosFinalizadosDoContato[index].tags?.filter(
              tag => tag.id !== tagId,
            );
        }

        if (atendimentoContato) {
          atendimentoContato.tags = atendimentoContato.tags?.filter(
            tag => tag.id !== tagId,
          );
        }
      }

      state.loadingRemoveTagAtendimento = false;
    });
    builder.addCase(fetchRemoveTagAtendimento.rejected, state => {
      state.loadingRemoveTagAtendimento = false;
    });

    // Transferir atendimento
    builder.addCase(transferirAtendimento.pending, state => {
      state.loadingTransferirAtendimento = true;
    });
    builder.addCase(transferirAtendimento.fulfilled, state => {
      state.atendimentoSelecionado = initialState.atendimentoSelecionado;
      state.loadingTransferirAtendimento = false;
    });
    builder.addCase(transferirAtendimento.rejected, state => {
      state.loadingTransferirAtendimento = false;
    });

    // Aceitar transferência
    builder.addCase(fetchAceitarTransferencia.pending, state => {
      state.loadingAceitarTransferencia = true;
    });
    builder.addCase(fetchAceitarTransferencia.fulfilled, (state, action) => {
      state.loadingAceitarTransferencia = false;
      const { id } = action.payload;
      state.isTransferenciaAceita = true;

      state.transferencias = state.transferencias.filter(
        transferencia => transferencia.id !== id,
      );
    });
    builder.addCase(fetchAceitarTransferencia.rejected, state => {
      state.loadingAceitarTransferencia = false;
    });

    // Recusar transferência
    builder.addCase(fetchRecusarTransferencia.pending, state => {
      state.loadingRecusarTransferencia = true;
    });
    builder.addCase(fetchRecusarTransferencia.fulfilled, (state, action) => {
      const { id } = action.payload;
      state.transferencias = state.transferencias.filter(
        transferencia => transferencia.id !== id,
      );
      state.showModalTransferenciaEnviada = false;
      state.loadingRecusarTransferencia = false;
    });
    builder.addCase(fetchRecusarTransferencia.rejected, state => {
      state.loadingRecusarTransferencia = false;
    });

    // Cancelar transferência
    builder.addCase(fetchCancelarTransferencia.pending, state => {
      state.loadingCancelarTransferencia = true;
    });
    builder.addCase(fetchCancelarTransferencia.fulfilled, (state, action) => {
      const { id } = action.payload;
      state.loadingCancelarTransferencia = false;
      const { atendimentoSelecionado } = state;

      // Verifica se o modal de transferência enviada está aberto e fecha
      if (state.showModalTransferenciaEnviada)
        state.showModalTransferenciaEnviada = false;

      /**
       * Ao cancelar uma transferência. O [atendimento selecionado] é atualizado
       * atualizando o atributo [transferenciaPendenteId] da transferência.
       * E também a lista de [atendimentos iniciados]
       * e percorrida para atualizar o atendimento em questão.
       */
      if (atendimentoSelecionado) {
        if (atendimentoSelecionado.transferenciaPendenteId === id)
          atendimentoSelecionado.transferenciaPendenteId = undefined;
        state.atendimentosIniciados.forEach(atendimento => {
          if (atendimento.transferenciaPendenteId === id) {
            atendimento.transferenciaPendenteId = undefined;
          }
        });
      }
    });
    builder.addCase(fetchCancelarTransferencia.rejected, state => {
      state.loadingCancelarTransferencia = false;
    });

    // Fixar atendimento
    builder.addCase(fixarAtendimento.pending, state => {
      state.loadingFixarAtendimento = true;
    });
    builder.addCase(fixarAtendimento.fulfilled, state => {
      state.loadingFixarAtendimento = false;
    });
    builder.addCase(fixarAtendimento.rejected, state => {
      state.loadingFixarAtendimento = false;
    });

    // Desafixar atendimento
    builder.addCase(desafixarAtendimento.pending, state => {
      state.loadingDesafixarAtendimento = true;
    });
    builder.addCase(desafixarAtendimento.fulfilled, state => {
      state.loadingDesafixarAtendimento = false;
    });
    builder.addCase(desafixarAtendimento.rejected, state => {
      state.loadingDesafixarAtendimento = false;
    });

    builder.addCase(fetchAtendimentosEmAndamento.pending, (state, action) => {
      state.loadingAtendimentosEmAndamento = true;
      state.activeAtendimentosEmAndamentoRequestId = action.meta.requestId;
    });
    builder.addCase(fetchAtendimentosEmAndamento.fulfilled, (state, action) => {
      if (
        state.activeAtendimentosEmAndamentoRequestId !== action.meta.requestId
      ) {
        return;
      }

      state.loadingAtendimentosEmAndamento = false;
      state.activeAtendimentosEmAndamentoRequestId = undefined;
      const { cursor } = action.meta.arg || {};

      if (!cursor || !cursor.afterCursor) {
        state.atendimentosEmAndamento = action.payload.data;
      } else {
        state.atendimentosEmAndamento = state.atendimentosEmAndamento.concat(
          action.payload.data,
        );
      }
      state.atendimentosEmAndamentoCursor = action.payload.cursor;
    });
    builder.addCase(fetchAtendimentosEmAndamento.rejected, (state, action) => {
      if (
        state.activeAtendimentosEmAndamentoRequestId !== action.meta.requestId
      ) {
        return;
      }

      state.loadingAtendimentosEmAndamento = false;
      state.activeAtendimentosEmAndamentoRequestId = undefined;
    });

    // Reasons
    builder.addCase(fetchAtendimentoReasons.pending, state => {
      state.loadingReasonsAtendimento = true;
    });
    builder.addCase(fetchAtendimentoReasons.fulfilled, (state, action) => {
      state.loadingReasonsAtendimento = false;
      state.reasonsAtendimento = action.payload;
    });
    builder.addCase(fetchAtendimentoReasons.rejected, state => {
      state.loadingReasonsAtendimento = false;
    });

    // Finish reason setting
    builder.addCase(fetchFinishReasonSetting.pending, state => {
      state.loadingFinishReasonSetting = true;
    });
    builder.addCase(fetchFinishReasonSetting.fulfilled, (state, action) => {
      state.loadingFinishReasonSetting = false;
      state.isRequiredMotivoAtendimento =
        action.payload.isRequiredMotivoAtendimento;
    });
    builder.addCase(fetchFinishReasonSetting.rejected, state => {
      state.loadingFinishReasonSetting = false;
    });

    // Marcar atendimento como lido
    builder.addCase(fetchMarcarAtendimentoComoLido.pending, state => {
      state.loadingMarcarAtendimentoComoLido = true;
    });
    builder.addCase(fetchMarcarAtendimentoComoLido.fulfilled, state => {
      state.loadingMarcarAtendimentoComoLido = false;
    });
    builder.addCase(fetchMarcarAtendimentoComoLido.rejected, state => {
      state.loadingMarcarAtendimentoComoLido = false;
    });

    // Marcar atendimento como não lido
    builder.addCase(fetchMarcarAtendimentoComoNaoLido.pending, state => {
      state.loadingMarcarAtendimentoComoNaoLido = true;
    });
    builder.addCase(fetchMarcarAtendimentoComoNaoLido.fulfilled, state => {
      state.loadingMarcarAtendimentoComoNaoLido = false;
    });
    builder.addCase(fetchMarcarAtendimentoComoNaoLido.rejected, state => {
      state.loadingMarcarAtendimentoComoNaoLido = false;
    });
    // Marcar todos os atendimentos como lidos
    builder.addCase(fetchMarcarTodosAtendimentosComoLidos.pending, state => {
      state.loadingMarcarTodosAtendimentosComoLidos = true;
    });
    builder.addCase(fetchMarcarTodosAtendimentosComoLidos.fulfilled, state => {
      state.loadingMarcarTodosAtendimentosComoLidos = false;
    });
    builder.addCase(fetchMarcarTodosAtendimentosComoLidos.rejected, state => {
      state.loadingMarcarTodosAtendimentosComoLidos = false;
    });
  },
});

// actions

export const {
  setLimparTransferenciaRecebida,
  setAtendimentoDeAtividadeSelecionado,
  setSearchTermFilaDeEspera,
  setOrderByFilaDeEspera,
  setDrawerFilaDeEsperaAberto,
  setshowModalAlertaInatividade,
  setAtendimentoSelecionado,
  setHistoricoAtendimentoSelecionado,
  setHistoricoAtendimentoNaFila,
  setAtendenteSelecionado,
  setAtendenteSelecionadoV2,
  updateAtendimentoSelecionado,
  updateListaDeIniciados,
  updateListaTransferencias,
  onCloseModalTransferenciaEnviada,
  setTransferenciaRecebida,
  updateListaDeIniciadosCancelarTransferencia,
  updateListaDeIniciadosAlterarSessao,
  updateSessaoExpiraEm,
  updateListaDeIniciadosBloqueioContato,
  updateListaDeIniciadosModoEnvio,
  checkAtendimentoTransferencias,
  setTotalMensagensNaoLidasAtendimentoIniciado,
  resetListaAtendimentosNaFila,
  resetListaAtendimentosNaFilaV2,
  resetAtendimentosConcluidos,
  resetMostrarAlertaDeInatividade,
  resetAtendimentosEmAndamentoDoContato,
  resetAtendimentosFinalizadosDoContato,
  resetAtendimentosFinalizados,
  updateListaDeiniciadosAlterarTagContato,
  resetAtendimentoContato,
  resetAtendimentosEmAndamento,
  setMarcarAtendimentoComoLido,
  setMarcarAtendimentoComoNaoLido,
  setMarcarTodosAtendimentosComoLidos,
} = atendimentosSlice.actions;

// selectors

export const atendimentosSelector = (state: RootState) => state.atendimentos;

export default atendimentosSlice.reducer;
